import { forwardRef, HTMLProps, useRef } from 'react'
import clsx from 'classnames'
import { useTranslations } from 'next-intl'
import { useIntersection } from '@/shared/utils'

import { AnalyticCard } from '@/entities/analytic-card'
import { Text } from '@/shared/ui'
import { IPostPreview } from '@/interfaces/analytics'
import { AnalyticsLoader } from '@/features/analytics-list/analytics-loader'
import style from './analytics-list.module.scss'

type AnalyticsAsyncListProps = HTMLProps<HTMLUListElement> & {
  cards: Omit<IPostPreview, 'blocks'>[]
  publications?: boolean
  anchorId?: string
  onCurrentPage?: (page: number) => void
  onFetchPage?: (page: number) => void
}

export const AnalyticsAsyncList = forwardRef<HTMLUListElement, AnalyticsAsyncListProps>((props, ref) => {
  const { cards, anchorId, publications, onCurrentPage, onFetchPage, ...restProps } = props
  const listRef = useRef<HTMLUListElement>(null)
  const translate = useTranslations()
  useIntersection({
    ref: listRef,
    onCurrentPage: page => {
      onCurrentPage?.(page)
    },
    options: {
      rootMargin: '-15% 0px -80% 0px',
    },
  })
  useIntersection({
    ref: listRef,
    onCurrentPage: (page, observerInstance) => {
      if (cards?.length) {
        observerInstance.disconnect()
      } else {
        onFetchPage?.(page)
      }
    },
    options: {
      rootMargin: '160% 0px 70% 0px',
    },
  })

  if (cards?.length === 0)
    return (
      <div className={style.empty}>
        <Text type="h1" color="dark">
          {translate('analyticsNotFound')}
        </Text>
      </div>
    )

  return (
    <ul
      id={anchorId}
      style={cards ? {} : { minHeight: '110vh' }}
      ref={el => {
        // @ts-ignore
        ref?.(el)
        // @ts-ignore
        listRef.current = el
      }}
      className={style.list}
      {...restProps}
    >
      {cards ? (
        cards.map(({ category, slug, date_published, title, image_preview, type, external_url }, index) => (
          <AnalyticCard
            className={clsx(style.item, { [style.publications]: publications })}
            key={index}
            title={title}
            date={date_published}
            category={category}
            image={image_preview}
            index={index}
            slug={slug}
            type={type}
            external_url={external_url}
          />
        ))
      ) : (
        <AnalyticsLoader className={clsx(style.item, style.publications, { [style.publications]: publications })} />
      )}
    </ul>
  )
})

AnalyticsAsyncList.displayName = 'AnalyticsAsyncList'
